import type { Locker, LockerAssignment, LockerRoom } from '@/types';

export function formatRoom(room: LockerRoom): string {
  return room === 'male' ? 'Male' : 'Female';
}

/**
 * The locker room a member's gender names, or null when it names none.
 * 'prefer_not_to_say' / null / anything else means we can't know which room to
 * offer, so those members get no in-context picker and are sent to the Lockers
 * page, where the admin picks the locker first.
 *
 * Shared so CheckInModal and GiveLockerKeyModal can never disagree about which
 * members see a picker.
 */
export function lockerRoomForGender(gender: string | null | undefined): LockerRoom | null {
  return gender === 'male' || gender === 'female' ? gender : null;
}

/** One room's lockers, preserving the (room, sort_order) fetch order. */
export function lockersInRoom(lockers: Locker[] | undefined, room: LockerRoom): Locker[] {
  return (lockers ?? []).filter((locker) => locker.room === room);
}

/**
 * Open assignment per locker id — the single definition of "occupied" shared
 * by every locker board (Lockers page, check-in picker).
 */
export function openAssignmentsByLockerId(
  assignments: LockerAssignment[] | undefined,
): Map<string, LockerAssignment> {
  return new Map(
    (assignments ?? [])
      // Callers currently pass already-open rows, but enforce the "open"
      // contract here so a historical list can never mark lockers occupied.
      .filter((assignment) => assignment.returned_at === null)
      .map((assignment) => [assignment.locker_id, assignment]),
  );
}

/**
 * Compact "held for" duration, e.g. "42m", "1h 05m", "2d 3h". Used everywhere a
 * key's usage time is shown so the format stays consistent.
 */
export function formatHeldDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

/** Seconds a still-open assignment has been held, as of `now`. */
export function heldSecondsSince(assignedAt: string, now: number): number {
  return Math.max(0, Math.round((now - new Date(assignedAt).getTime()) / 1000));
}

/** Seconds between assignment and return for a completed assignment. */
export function heldSecondsBetween(assignedAt: string, returnedAt: string): number {
  return Math.max(0, Math.round((new Date(returnedAt).getTime() - new Date(assignedAt).getTime()) / 1000));
}

export function memberDisplayName(member?: { first_name?: string; last_name?: string } | null): string {
  if (!member?.first_name && !member?.last_name) return 'Unknown Member';
  return `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim();
}
